import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './password-requirement-box.module.scss';

interface PasswordRequirement {
  id: string;
  label: string;
  validator: (password: string) => boolean;
}

interface PasswordRequirementsProps {
  password: string;
  className?: string;
}

const PasswordRequirementsBox: React.FC<PasswordRequirementsProps> = ({
  password,
  className = '',
}) => {
  const { t } = useTranslation();
  const requirements: PasswordRequirement[] = [
    {
      id: 'alphanumeric',
      label: t('auth.alphanumericCharacters'),
      validator: (pwd) => /[a-zA-Z]/.test(pwd) && /[0-9]/.test(pwd),
    },
    {
      id: 'special',
      label: t('auth.oneSpecialCharacter'),
      validator: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
    },
    {
      id: 'length',
      label: t('auth.atLeastTenCharacters'),
      validator: (pwd) => pwd.length >= 10,
    },
    {
      id: 'uppercase',
      label: t('auth.oneUppercaseLetter'),
      validator: (pwd) => /[A-Z]/.test(pwd),
    },
    {
      id: 'lowercase',
      label: t('auth.web.oneLowercaseLetter'),
      validator: (pwd) => /[a-z]/.test(pwd),
    },
  ];

  const isRequirementMet = (requirement: PasswordRequirement): boolean => {
    return requirement.validator(password);
  };

  const getRequirementsStatus = () => {
    return requirements.map(req => ({
      ...req,
      met: isRequirementMet(req),
    }));
  };

  const requirementsStatus = getRequirementsStatus();

  return (
    <div className={`${styles.container} ${className}`}>
      <div className={styles.requirementsList}>
        {requirementsStatus.map((requirement) => (
          <div
            key={requirement.id}
            className={`${styles.requirement} ${
              requirement.met ? styles.met : ''
            }`}
          >
            {/* ICON: Checkmark or Empty Circle */}
            <div className={styles.icon}>
              {requirement.met ? (
                // Checkmark Icon
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" fill="currentColor" />
                  <path
                    d="M6 10L9 13L14 7"
                    stroke="var(--app-primary-text-08)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                // Empty Circle Icon
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle
                    cx="10"
                    cy="10"
                    r="9"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                  />
                </svg>
              )}
            </div>

            <span className={styles.label}>{requirement.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PasswordRequirementsBox;